import { PROFILE_NAME_PATTERN, ALLOWED_NAME_CHARS } from "../geo/blocks";
import type { CaptureBlock, CaptureNotebook } from "../types";

export type ExportFormat = "json" | "cli" | "ts";

/** Keep only the characters the CLI profile-name validator allows. */
export function sanitizeName(raw: string): string {
  const cleaned = raw.trim().replace(ALLOWED_NAME_CHARS, "-").replace(/-+/g, "-");
  // Must start with an alphanumeric.
  return cleaned.replace(/^[^a-zA-Z0-9]+/, "");
}

export function isValidName(raw: string): boolean {
  return PROFILE_NAME_PATTERN.test(raw.trim()) && raw.trim().length <= 64;
}

export function formatCoord(n: number, digits = 7): string {
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

export function labelFor(block: CaptureBlock): string {
  return block.label ?? block.name;
}

export function cliCommand(block: CaptureBlock): string {
  const name = block.name.replace(/ /g, "-");
  const label = block.label ? ` --label "${block.label.replace(/"/g, '\\"')}"` : "";
  return `attenducsi location create ${name} --lat ${formatCoord(block.latitude, 9)} --lon ${formatCoord(block.longitude, 9)}${label}`;
}

export function tsSnippet(blocks: CaptureBlock[], version: string): string {
  const rows = blocks
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((b) => {
      const label = b.label ? `label: ${JSON.stringify(b.label)}, ` : "";
      return `  {\n    name: ${JSON.stringify(b.name)},\n    ${label}latitude: ${formatCoord(b.latitude, 9)},\n    longitude: ${formatCoord(b.longitude, 9)},\n    version: ${JSON.stringify(version)},\n    verified: false,\n  },`;
    })
    .join("\n\n");

  return `// Generated from the capture app — feed into packages/cli/src/shared/location/builtin.ts
// After a check-in actually succeeds from a spot, flip its verified: true.
import type { BuiltinLocationProfile } from "shared/location/builtin";

export const BUILTIN_PROFILES: BuiltinLocationProfile[] = [
${rows}
];
`;
}

export function jsonExport(notebook: CaptureNotebook): string {
  return JSON.stringify(
    {
      app: "attenducsi-web",
      exportedAt: new Date().toISOString(),
      bounds: {
        minLat: 3.05,
        maxLat: 3.11,
        minLon: 101.71,
        maxLon: 101.76,
      },
      captures: notebook.blocks,
    },
    null,
    2,
  );
}

export function renderExport(notebook: CaptureNotebook, format: ExportFormat, version: string): string {
  switch (format) {
    case "cli":
      return notebook.blocks.map(cliCommand).join("\n");
    case "ts":
      return tsSnippet(notebook.blocks, version);
    case "json":
      return jsonExport(notebook);
  }
}

export function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}